import { Component, ViewChild, inject, input, output, signal } from '@angular/core';

import { Menu, MenuComponent, MenuIcon } from '@camelot/menu';
import { ButtonToolComponent, CamOverlayPanelComponent, LoaderComponent } from '@camelot/ui';
import { CamBaseComponent, extractEnum } from '@camelot/utils';
import { WysiswgBlockData, convertBlocksToHtml } from '@camelot/wysiswyg';

import { CamCommunicationsAiService } from '../../services/ai.service';
import { CamCommunicationsService } from '../../services/communications.service';
import { CommunicationType } from '../../services/dto/communication';
import { Tones } from '../../services/dto/tones';

@Component({
  selector: 'cam-conversations-ai-tools',
  imports: [ButtonToolComponent, MenuComponent, CamOverlayPanelComponent, LoaderComponent],
  templateUrl: './ai-tools.component.html',
  styleUrl: './ai-tools.component.scss',
})
export class AiToolsComponent extends CamBaseComponent {
  taskId = input.required<string>();
  responseToCommunicationId = input<string>();
  message = input<{ blocks: WysiswgBlockData[] } | null>(null);

  generatedMessage = output<{ blocks: string; saveAfter?: boolean }>();
  changedType = output<string>();

  @ViewChild(CamOverlayPanelComponent, { static: true })
  overlay!: CamOverlayPanelComponent;

  public actionMenu = signal<Menu | null>(null);

  private _conversationService = inject(CamCommunicationsService);
  private _aiServices = inject(CamCommunicationsAiService);

  // projeté dans cam-loader : le composant existe avant la fin du fetch, l'id doit être relu à chaque action.
  private _getConversationId(): string | null {
    return this._conversationService.communicationByTaskId.get(this.taskId())?.id ?? null;
  }

  public openMenu() {
    this._updateMenu();

    this.overlay.open(true);
  }

  public askSummary(send: boolean = false) {
    const conversationId = this._getConversationId();
    if (!conversationId) {
      return;
    }
    this._startRequest();
    this._aiServices.aiConversationSummary$(conversationId).subscribe({
      next: (message: string) => {
        this.generatedMessage.emit({ blocks: message, saveAfter: send });
        this.changedType.emit(CommunicationType.Intern.toString());
      },
      complete: () => this.requestState.completed(),
    });
  }

  public askAnswer() {
    const conversationId = this._getConversationId();
    const responseToCommunicationId = this.responseToCommunicationId();
    if (!conversationId || !responseToCommunicationId) {
      return;
    }
    this._startRequest();
    this._aiServices.aiConversationAnswer$(conversationId, responseToCommunicationId).subscribe({
      next: (message: string) => this.generatedMessage.emit({ blocks: message }),
      complete: () => this.requestState.completed(),
    });
  }

  public askReformulation(tone: Tones) {
    const message = this.message();
    if (!this._getConversationId() || !message || message.blocks.length === 0) {
      return;
    }
    this._startRequest();
    this._aiServices.aiMessageReformulation$(convertBlocksToHtml(message.blocks), tone).subscribe({
      next: (message: string) => this.generatedMessage.emit({ blocks: message }),
      complete: () => this.requestState.completed(),
    });
  }

  private _startRequest() {
    this.requestState.asked();
    this.overlay.close();
  }

  private _updateMenu() {
    const menu = [
      new MenuIcon({
        key: 'summary-send',
        label: 'communication.ai.summary-n-send',
        style: 'dark',
        icon: 'article-line',
        iconsColor: 'icon-color-icon-tertiary',
        callback: () => this.askSummary(true),
      }),
      new MenuIcon({
        key: 'summary',
        label: 'communication.ai.summary',
        style: 'dark',
        icon: 'article-line',
        iconsColor: 'icon-color-icon-tertiary',
        callback: () => this.askSummary(),
      }),
      new MenuIcon({
        key: 'answer',
        label: 'communication.ai.answer',
        style: 'dark',
        icon: 'modify',
        iconsColor: 'icon-color-icon-tertiary',
        disabled: !this.responseToCommunicationId(),
        callback: () => this.askAnswer(),
      }),
      ...extractEnum(Tones, true).map(
        ({ value, name }) =>
          new MenuIcon({
            key: 'reformulation-' + name,
            label: 'communication.ai.reformulation.' + name.toLocaleLowerCase(),
            style: 'dark',
            icon: 'modify',
            iconsColor: 'icon-color-icon-tertiary',
            disabled: (this.message()?.blocks?.length ?? 0) === 0,
            callback: () => this.askReformulation(value),
          })
      ),
    ];

    this.actionMenu.set(
      new Menu({
        elements: menu,
        direction: 'vertical',
      })
    );
  }
}
